import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { BottomSheet } from '@/components/BottomSheet';
import { Input } from '@/components/primitives/Input';
import { Button } from '@/components/primitives/Button';
import { SegmentedControl } from '@/components/primitives/SegmentedControl';
import { Checkbox } from '@/components/primitives/Checkbox';
import { Toggle } from '@/components/primitives/Toggle';
import { useDatabase, useCreateDatabase, useUpdateDatabase, useTestConnection, } from '@/api/useDatabases';
import { useStorages } from '@/api/useStorages';
import { ConnectionTestStrip } from '@/components/ConnectionTestResult';
import { presetToCron, cronToPreset, describeCron, nextRunsLabel } from '@/lib/cron';
import s from './DatabaseFormSheet.module.css';
const DEFAULT_STATE = {
    name: '',
    description: '',
    host: '',
    port: 5432,
    dbname: '',
    username: '',
    password: '',
    ssl_mode: 'prefer',
    preset: '4h',
    cron: presetToCron('4h'),
    storages: {},
    enabled: true,
    self_test: true,
    critical: false,
};
export function DatabaseFormSheet({ mode, databaseId, onClose }) {
    const existing = useDatabase(databaseId);
    const storages = useStorages();
    const create = useCreateDatabase();
    const update = useUpdateDatabase(databaseId ?? '');
    const test = useTestConnection();
    const [submitErr, setSubmitErr] = useState(null);
    const [hasSeeded, setHasSeeded] = useState(false);
    const initial = useMemo(() => {
        if (mode === 'edit' && existing.data) {
            const e = existing.data;
            const linked = e.storages ?? [];
            const retention = e.retention_days_per_storage ?? {};
            const stMap = {};
            (storages.data ?? []).forEach((st) => {
                const selected = linked.some((x) => x.id === st.id);
                stMap[st.id] = {
                    selected,
                    retention: retention[st.id] ?? st.retention_days_default,
                };
            });
            return {
                name: e.name,
                description: e.description ?? '',
                host: e.host,
                port: e.port,
                dbname: e.dbname,
                username: e.username,
                password: '',
                ssl_mode: e.ssl_mode,
                preset: cronToPreset(e.schedule_cron),
                cron: e.schedule_cron,
                storages: stMap,
                enabled: e.enabled,
                self_test: false,
                critical: false,
            };
        }
        const stMap = {};
        (storages.data ?? []).forEach((st) => {
            stMap[st.id] = { selected: false, retention: st.retention_days_default };
        });
        return { ...DEFAULT_STATE, storages: stMap };
    }, [mode, existing.data, storages.data]);
    const [form, setForm] = useState(initial);
    useEffect(() => {
        if (!hasSeeded && (mode === 'create' ? storages.data : existing.data && storages.data)) {
            setForm(initial);
            setHasSeeded(true);
        }
    }, [hasSeeded, mode, storages.data, existing.data, initial]);
    const setF = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
    const runTest = () => {
        test.mutate({
            host: form.host,
            port: form.port,
            dbname: form.dbname,
            username: form.username,
            password: form.password,
            ssl_mode: form.ssl_mode,
        });
    };
    const handleSubmit = (closeAfter) => {
        setSubmitErr(null);
        const retention = {};
        const storageIds = [];
        Object.entries(form.storages).forEach(([id, v]) => {
            if (v.selected) {
                storageIds.push(id);
                retention[id] = v.retention;
            }
        });
        const cron = form.preset === 'custom' ? form.cron : presetToCron(form.preset);
        const payload = {
            name: form.name,
            description: form.description || null,
            host: form.host,
            port: form.port,
            dbname: form.dbname,
            username: form.username,
            password: form.password,
            ssl_mode: form.ssl_mode,
            schedule_cron: cron,
            enabled: form.enabled,
            storage_ids: storageIds,
            retention_days_per_storage: retention,
        };
        const onError = (e) => setSubmitErr(e.message);
        if (mode === 'create') {
            create.mutate(payload, {
                onSuccess: () => {
                    if (closeAfter)
                        onClose();
                    else
                        setForm({ ...DEFAULT_STATE, storages: form.storages });
                },
                onError,
            });
        }
        else {
            update.mutate(payload, {
                onSuccess: () => onClose(),
                onError,
            });
        }
    };
    return (_jsxs(BottomSheet, { open: true, title: mode === 'create' ? 'Add database' : 'Edit database', onClose: onClose, footer: _jsxs(_Fragment, { children: [_jsx(Button, { variant: "ghost", onClick: onClose, children: "Cancel" }), _jsxs("div", { style: { display: 'flex', gap: 8 }, children: [mode === 'create' && (_jsx(Button, { variant: "ghost", onClick: () => handleSubmit(false), children: "Save & add another" })), _jsx(Button, { onClick: () => handleSubmit(true), children: mode === 'create' ? 'Create database' : 'Save changes' })] })] }), children: [submitErr && _jsx("div", { className: s.errBlock, children: submitErr }), _jsxs("div", { className: s.section, children: [_jsx("div", { className: s.sectionHead, children: "Identity" }), _jsxs("div", { className: s.field, children: [_jsx("label", { className: s.label, htmlFor: "db-name", children: "Name" }), _jsx(Input, { id: "db-name", value: form.name, onChange: (e) => setF('name', e.target.value.toLowerCase()), placeholder: "prod-main" })] }), _jsxs("div", { className: s.field, children: [_jsx("label", { className: s.label, htmlFor: "db-desc", children: "Description" }), _jsx(Input, { id: "db-desc", value: form.description, onChange: (e) => setF('description', e.target.value), placeholder: "Production primary" })] })] }), _jsxs("div", { className: s.section, children: [_jsx("div", { className: s.sectionHead, children: "Connection" }), _jsxs("div", { className: s.cols2, children: [_jsxs("div", { className: s.field, children: [_jsx("label", { className: s.label, htmlFor: "db-host", children: "Host" }), _jsx(Input, { id: "db-host", value: form.host, onChange: (e) => setF('host', e.target.value), placeholder: "pg.internal" })] }), _jsxs("div", { className: s.field, children: [_jsx("label", { className: s.label, htmlFor: "db-port", children: "Port" }), _jsx(Input, { id: "db-port", type: "number", value: form.port, onChange: (e) => setF('port', Number(e.target.value)) })] })] }), _jsxs("div", { className: s.cols3, children: [_jsxs("div", { className: s.field, children: [_jsx("label", { className: s.label, htmlFor: "db-dbname", children: "Database" }), _jsx(Input, { id: "db-dbname", value: form.dbname, onChange: (e) => setF('dbname', e.target.value) })] }), _jsxs("div", { className: s.field, children: [_jsx("label", { className: s.label, htmlFor: "db-user", children: "User" }), _jsx(Input, { id: "db-user", value: form.username, onChange: (e) => setF('username', e.target.value) })] }), _jsxs("div", { className: s.field, children: [_jsx("label", { className: s.label, htmlFor: "db-pass", children: "Password" }), _jsx(Input, { id: "db-pass", type: "password", value: form.password, onChange: (e) => setF('password', e.target.value), placeholder: mode === 'edit' ? 'unchanged' : '' })] })] }), _jsxs("div", { className: s.field, children: [_jsx("label", { className: s.label, children: "SSL mode" }), _jsx(SegmentedControl, { value: form.ssl_mode, onChange: (v) => setF('ssl_mode', v), options: [
                                    { value: 'verify-full', label: 'verify-full' },
                                    { value: 'verify-ca', label: 'verify-ca' },
                                    { value: 'require', label: 'require' },
                                    { value: 'prefer', label: 'prefer' },
                                ] })] }), _jsx("div", { style: { display: 'flex', justifyContent: 'flex-start' }, children: _jsx(Button, { variant: "ghost", onClick: runTest, disabled: test.isPending, children: test.isPending ? 'Testing…' : 'Test connection' }) }), (test.isPending || test.data || test.error) && (_jsx(ConnectionTestStrip, { pending: test.isPending, result: test.data
                            ? test.data
                            : test.error
                                ? { ok: false, latency_ms: 0, error: test.error.message }
                                : null }))] }), _jsxs("div", { className: s.section, children: [_jsx("div", { className: s.sectionHead, children: "Schedule" }), _jsx("div", { className: s.field, children: _jsx(SegmentedControl, { value: form.preset, onChange: (v) => {
                                setF('preset', v);
                                if (v !== 'custom')
                                    setF('cron', presetToCron(v));
                            }, options: [
                                { value: '2h', label: '2h' },
                                { value: '4h', label: '4h' },
                                { value: '6h', label: '6h' },
                                { value: '12h', label: '12h' },
                                { value: '24h', label: '24h' },
                                { value: 'custom', label: 'Custom' },
                            ] }) }), form.preset === 'custom' && (_jsxs("div", { className: s.field, children: [_jsx("label", { className: s.label, htmlFor: "db-cron", children: "Cron expression" }), _jsx(Input, { id: "db-cron", mono: true, value: form.cron, onChange: (e) => setF('cron', e.target.value), placeholder: "0 */4 * * *" }), _jsx("div", { className: s.help, children: describeCron(form.cron) })] })), _jsxs("div", { className: s.nextRuns, children: ["Next runs:", ' ', nextRunsLabel(form.preset === 'custom' ? form.cron : presetToCron(form.preset))] })] }), _jsxs("div", { className: s.section, children: [_jsx("div", { className: s.sectionHead, children: "Storage targets" }), (storages.data ?? []).map((st) => {
                        const v = form.storages[st.id] ?? { selected: false, retention: st.retention_days_default };
                        return (_jsxs("div", { className: s.stRow, children: [_jsx(Checkbox, { checked: v.selected, onChange: (next) => setF('storages', { ...form.storages, [st.id]: { ...v, selected: next } }) }), _jsxs("div", { style: { flex: 1 }, children: [_jsx("div", { className: s.stName, children: st.name }), _jsxs("div", { className: s.stMeta, children: [st.endpoint_url, " / ", st.bucket] })] }), _jsx(Input, { mono: true, type: "number", value: v.retention, onChange: (e) => setF('storages', {
                                        ...form.storages,
                                        [st.id]: { ...v, retention: Number(e.target.value) },
                                    }), className: s.retention }), _jsx("span", { className: s.stMeta, children: "days" })] }, st.id));
                    })] }), _jsxs("div", { className: s.section, children: [_jsx("div", { className: s.sectionHead, children: "Options" }), _jsxs("div", { className: s.optsRow, children: [_jsx("span", { className: s.optsLabel, children: "Enabled" }), _jsx(Toggle, { on: form.enabled, onChange: (v) => setF('enabled', v), "aria-label": "Enabled" })] }), _jsxs("div", { className: s.optsRow, children: [_jsx("span", { className: s.optsLabel, children: "Self-test backup right after creation" }), _jsx(Toggle, { on: form.self_test, onChange: (v) => setF('self_test', v), "aria-label": "Self-test" })] }), _jsxs("div", { className: s.optsRow, children: [_jsx("span", { className: s.optsLabel, children: "Critical priority" }), _jsx(Toggle, { on: form.critical, onChange: (v) => setF('critical', v), "aria-label": "Critical" })] })] })] }));
}
